import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { logoForTheme, APP_NAME } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LayoutDashboard, Calendar, Building2, Users, Target, GitBranch, Settings, DollarSign, LogOut, Shield, Bot, UserCog, Menu, X, Layers, Video, BarChart3, Megaphone, Sun, Moon, Mail, Car, Wand2, Sparkles, Package, Tag, CreditCard, BookOpen, MessageSquare, Globe, Database, Workflow, FolderOpen, Gauge } from 'lucide-react';
import { useState, useEffect } from 'react';

// Se exporta para que el editor de Tipos de Usuario pueda calcular, con ESTA misma
// lista y ESTE mismo predicado, qué entradas va a esconder un tipo antes de guardarlo.
// Duplicar la lista allá habría creado una segunda fuente de verdad justo en el aviso
// que existe para que nadie apague medio sistema sin darse cuenta.
export const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboards', icon: LayoutDashboard, perm: null, moduleKey: 'dashboard' },
  { path: '/properties', label: 'Propiedades', icon: Building2, perm: 'properties', hiddenFor: ['developer'], moduleKey: 'properties' },
  { path: '/products', label: 'Productos y Servicios', icon: Package, perm: null, hiddenFor: ['developer'], moduleKey: 'products' },
  { path: '/brands', label: 'Marcas', icon: Tag, perm: null, moduleKey: 'brands' },
  { path: '/developments', label: 'Desarrollos', icon: Layers, perm: 'developments', onlyFor: ['developer', 'franchise'], moduleKey: 'developments' },
  { path: '/leads', label: 'Leads', icon: Target, perm: 'leads', moduleKey: 'leads' },
  { path: '/opportunities', label: 'Oportunidades', icon: GitBranch, perm: 'opportunities', moduleKey: 'opportunities' },
  { path: '/agenda', label: 'Agenda', icon: Calendar, perm: 'leads', moduleKey: 'agenda' },
  { path: '/campanas-masivas', label: 'WhatsApp / Voice Blaster', icon: Megaphone, perm: null, adminOnly: true, hiddenFor: ['independent'], moduleKey: 'mass_campaigns' },
  { path: '/correo', label: 'Correo', icon: Mail, perm: null, adminOnly: true, moduleKey: 'marketing' },
  { path: '/campana-ia-independiente', label: 'Campañas IA', icon: Sparkles, perm: null, adminOnly: true, moduleKey: 'ai_independent_campaigns' },
  // P11: Campañas IA vive en Configuración → Asistente IA (subtab).
  // La ruta sigue; el menú no la muestra.
  // Roles de Guardia ahora vive como tab dentro de Team Config
  // Productos (admin agencia/franquicia) vive como tab de Team Config (P3 higiene 26-ago).
  // Independiente y vendedor siguen viendo /products: no tienen Team Config.

  { path: '/commissions', label: 'Comisiones', icon: DollarSign, perm: null, moduleKey: 'commissions', onlyFor: ['agency', 'franchise', 'independent'] },
  { path: '/ai-agent', label: 'Conversaciones IA', icon: Bot, perm: 'ai_agents', adminOnly: false, moduleKey: 'ai_agent' },
  { path: '/biblioteca', label: 'Biblioteca', icon: FolderOpen, perm: 'ai_agents', adminOnly: false, moduleKey: 'file_library' },
  // 'Base de Conocimiento' retirada del menu el 20-ago-2026 por decision de
  // Sergio. Medido antes de quitarla: CERO documentos en las 20 empresas, y
  // el agente IA nunca la consultaba (ni una referencia en ai_agent.py ni en
  // ai_knowledge.py). Ya estaba oculta para todos menos superadmin.
  // La RUTA /knowledge-base sigue viva y sus endpoints tambien: esto solo
  // quita la entrada del menu. Retirar el codigo es un paso aparte, para no
  // chocar con quien este trabajando en server.js/App.js.
  { path: '/white-label', label: 'White Label', icon: Globe, perm: null, adminOnly: true, hiddenFor: ['independent', 'car_dealer'], moduleKey: 'landing' },
  { path: '/whatsapp', label: 'WhatsApp WABA', icon: MessageSquare, perm: null, adminOnly: true, moduleKey: 'waba' },
  { path: '/dev-hub', label: 'API Hub', icon: Database, perm: null, adminOnly: true, moduleKey: 'api_hub' },
  { path: '/logicas-recetas', label: 'Reglas de Comunicación', icon: Workflow, perm: null, adminOnly: true, moduleKey: 'comm_logics' },
  { path: '/car-dealer', label: 'Car-Dealer', icon: Car, perm: null, moduleKey: 'car_dealer' },
  { path: '/car-builder', label: 'Landing Page Builder', icon: Wand2, perm: null, moduleKey: 'car_builder', onlyFor: ['car_dealer'] },
  // Sin excepción hand-carved (a diferencia de car-builder): Dev Builder no es
  // "la landing única" del tenant developer, es un módulo adicional — el
  // gateo genérico (perfil.modules + allowedModules + onlyFor, ver
  // entradaVisible más abajo) ya alcanza, mismo patrón que '/developments'.
  { path: '/dev-builder', label: 'Landing Page Builder', icon: Wand2, perm: null, moduleKey: 'dev_builder', onlyFor: ['developer'] },
  { path: '/team-config', label: 'Team Config', icon: Users, perm: 'team', adminOnly: true, hiddenFor: ['independent'], moduleKey: 'team' },
  { path: '/academy', label: `${APP_NAME} Academy`, icon: Video, perm: null, moduleKey: 'academy' },
  { path: '/settings', label: 'Configuracion', icon: Settings, perm: 'settings', adminOnly: true, moduleKey: 'settings' },
  { path: '/settings/calificaciones-forecast', label: 'Calificaciones Forecast', icon: Gauge, perm: 'settings', adminOnly: true, moduleKey: 'cem_ratings' },
  { path: '/mi-tarjeta-virtual', label: 'Mi Tarjeta Virtual', icon: CreditCard, perm: null, adminOnly: false, moduleKey: null },
];

/**
 * ¿Se muestra esta entrada del menú? Función PURA a propósito: es la única fuente de
 * verdad de qué ve cada usuario, así que tiene que poder barrerse sobre datos reales
 * sin abrir un navegador (ver el barrido de menú del frente user-types).
 *
 * `ctx.perfil` es lo que devuelve `GET /me/permissions` — plan Y tipo ya resueltos.
 * Tres estados, y los tres importan:
 *
 *   - objeto  → GOBIERNA. `perfil.modules[moduleKey] === 'none'` esconde la entrada.
 *   - `undefined` (todavía cargando) → se aplica la regla HEREDADA como puente. Sin
 *     esto, en el primer pintado un usuario restringido vería entradas que no le
 *     tocan hasta que llega la respuesta: un parpadeo que además filtra.
 *   - `null` (falló la carga) → menú COMPLETO. Nadie se queda sin menú por un fallo
 *     de red; el fallo se registra donde se pide el perfil.
 *
 * Y si el perfil NO OPINA de un módulo (no está en el mapa, o la entrada no tiene
 * `moduleKey`), la entrada SE MUESTRA. "No sé" no es "no tiene" — es la misma regla
 * que en la red de compatibilidad, donde cerrar por omisión costó 893 pérdidas
 * medidas contra producción.
 */
export function entradaVisible(item, ctx) {
  const { user, impersonating, perfil } = ctx;
  const isVendor = user?.role === 'vendor';
  const isGuard = user?.role === 'guard';
  const perms = user?.permissions || {};
  const btype = user?.business_type || user?.company?.business_type || 'agency';
  const allowedModules = user?.allowed_modules || user?.company?.allowed_modules || {};
  const industry = user?.industry || user?.company?.industry || 'real_estate';
  const esCatalogo = (
    industry === 'education' || industry === 'generic' || industry === 'comercial' || industry === 'retail'
    || btype === 'education' || btype === 'generic' || btype === 'retail'
  );
  // SA4: llaves que antes colgaban de un padre. Si el plan/tipo viejo no las
  // declara, copian al padre — un tenant con settings ON sigue viendo WABA.
  const PADRE_DE = {
    agenda: 'leads',
    file_library: 'ai_agent',
    waba: 'settings',
    api_hub: 'settings',
    landing: 'settings',
    cem_ratings: 'settings',
  };
  const SA_SIEMPRE = new Set(['settings', 'waba', 'api_hub', 'landing', 'cem_ratings', 'capi']);
  const saSiempre = !!(impersonating && item.moduleKey && SA_SIEMPRE.has(item.moduleKey));
  const apagado = (mapa, key, noneVal) => {
    if (!key || !mapa) return false;
    if (mapa[key] === noneVal) return true;
    if (mapa[key] === undefined && PADRE_DE[key] && mapa[PADRE_DE[key]] === noneVal) return true;
    return false;
  };

  if (item.adminOnly && (isVendor || isGuard)) return false;
  // P11: Campañas IA no es menú de primer nivel. Vive en Configuración → Asistente IA.
  if (item.path === '/campana-ia-independiente') return false;
  // WS-IA-UNIFIED F3 — "Base de Conocimiento" se deprecó como entrada principal.
  // Su gestión vive ahora dentro de la config del Agente Principal. Se mantiene
  // visible SOLO para superadmin (incluso impersonando), con badge Legacy; la
  // ruta sigue activa por compat.
  if (item.legacy && !(user?.role === 'superadmin' || impersonating)) return false;
  if (isGuard && !['/', '/dashboard', '/team-config', '/properties'].includes(item.path)) return false;

  // Autos: Car Builder ES su landing. Chevrolet Cuautla vive en /l/{slug}
  // (HTML de Mauricio + inventario de Car Builder). El PERFIL de usuario
  // (perfil.modules.car_builder) NO puede esconderlo a propósito (ver test
  // "Car Dealer ve Car Builder..." en Sidebar.menu.test.mjs) — es la MISMA
  // excepción que ya aplica al módulo car_dealer, una fila abajo.
  // ⚠️ Lo que SÍ debe mirar este link es el flag de PLAN/licencia
  // (allowedModules.car_builder): el backend (routes/car_builder.py) exige ese
  // MISMO flag en sus 3 endpoints (enforce_module_access) — si el link se
  // muestra sin mirarlo, un tenant sin el override ve el link y entra a un
  // "Cargando..." infinito (car-builder-100-funcional, Fase 2).
  if (item.path === '/car-builder') {
    if (btype !== 'car_dealer') return false;
    if (allowedModules.car_dealer === false) return false;
    if (allowedModules.car_builder === false) return false;
    if (perfil && perfil.modules && perfil.modules.car_dealer === 'none') return false;
    return true;
  }
  if (item.path === '/white-label' && btype === 'car_dealer') return false;

  // ── El permiso: lo gobierna el PERFIL, no el campo heredado ────────────────
  if (!saSiempre && perfil && perfil.modules) {
    // Cubre también las 5 entradas que nunca tuvieron `perm` (Dashboards, Productos,
    // Marcas, Campañas Masivas, Mailings): antes NO había forma de esconderlas por
    // usuario. Todas tienen `moduleKey`, así que ahora sí.
    if (apagado(perfil.modules, item.moduleKey, 'none')) return false;
  } else if (!saSiempre && perfil === undefined) {
    // Puente mientras llega el perfil (ver la nota de arriba).
    if (item.perm && perms[item.perm] === 'none') return false;
  }

  if (!saSiempre && apagado(allowedModules, item.moduleKey, false)) return false;
  // Ocultar Productos para Car Dealer (su catálogo vive en /car-dealer)
  if (item.path === '/products' && (industry === 'car_dealer' || industry === 'automotive' || btype === 'car_dealer')) return false;
  // General / Educación: menú de primer nivel (inventario, como Propiedades).
  if (item.path === '/products' && !esCatalogo && !isVendor && !isGuard && btype !== 'independent') return false;
  // Industry filtering: hide properties/developments for non-real-estate
  if (industry && industry !== 'real_estate') {
    if (item.path === '/properties' || item.path === '/developments') return false;
  }
  // Business type filtering
  if (item.hiddenFor && item.hiddenFor.includes(btype)) return false;
  if (item.onlyFor && !item.onlyFor.includes(btype)) return false;
  return true;
}

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { user, logout, impersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isVendor = user?.role === 'vendor';
  // Sigue leyendo el campo heredado SOLO para el sello "solo lectura" de la entrada
  // (cosmético). La VISIBILIDAD ya no depende de él: la decide `entradaVisible`.
  const perms = user?.permissions || {};
  // undefined = cargando · null = falló · objeto = perfil resuelto (ver entradaVisible)
  const [perfil, setPerfil] = useState(undefined);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (setMobileOpen) setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // El menú se resuelve contra el perfil de permisos (plan Y tipo), no contra el
  // campo heredado. Si falla, `null` deja el menú completo a propósito.
  useEffect(() => {
    let vivo = true;
    api.get('/me/permissions')
      .then(({ data }) => { if (vivo) setPerfil(data && data.modules ? data : null); })
      .catch((e) => {
        if (vivo) setPerfil(null);
        console.error('[menu] no se pudo cargar /me/permissions; se muestra el menú completo', e);
      });
    return () => { vivo = false; };
  }, [user?.id, impersonating]);

  const visibleItems = NAV_ITEMS.filter(item => entradaVisible(item, { user, impersonating, perfil }));

  const API = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = user?.company_logo;
  const resolvedLogo = logoUrl ? (logoUrl.startsWith('http') ? logoUrl : `${API}${logoUrl}`) : null;

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-white/[0.04] flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="sidebar-logo">
          {resolvedLogo ? (
            <img src={resolvedLogo} alt="Logo" className="h-16 max-w-[180px] object-contain" data-testid="company-logo" />
          ) : (
            <img src={logoForTheme(theme)} alt="LEADCEM" className="h-8 w-auto object-contain" data-testid="platform-logo" />
          )}
        </Link>
                {user?.company_name && resolvedLogo && <p className="text-[9px] text-muted-foreground/50 mt-0.5 uppercase tracking-[2px] hidden lg:block">{user.company_name}</p>}
        {/* Close button for mobile */}
        <button onClick={() => setMobileOpen && setMobileOpen(false)} className="lg:hidden p-1 text-muted-foreground hover:text-white" data-testid="close-sidebar-btn">
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {user?.role === 'superadmin' && (
          <Link to="/admin" data-testid="nav-admin">
            <Button variant="ghost" className={cn("w-full justify-start gap-2.5 text-xs h-9 font-medium rounded-lg", location.pathname === '/admin' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-white hover:bg-white/[0.04]')}>
              <Shield className="w-4 h-4" /> Super Admin
            </Button>
          </Link>
        )}
        {visibleItems.map(item => (
          <Link key={item.path} to={item.path} data-testid={`nav-${item.path.slice(1)}`}>
            <Button variant="ghost" className={cn("w-full justify-start gap-2.5 text-xs h-9 font-medium rounded-lg", location.pathname === item.path ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-white hover:bg-white/[0.04]')}>
              <item.icon className="w-4 h-4" /> {item.label}
              {item.legacy && (
                <Badge variant="outline" className="ml-auto text-[8px] border-amber-500/30 text-amber-300/80 py-0 px-1">Legacy</Badge>
              )}
              {item.perm && isVendor && perms[item.perm] === 'read' && (
                <Badge variant="outline" className="ml-auto text-[8px] border-white/10 text-muted-foreground py-0 px-1">Solo lectura</Badge>
              )}
            </Button>
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-white/[0.04]">
        <div className="flex items-center gap-2 px-3 py-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-white truncate">{user?.name}</p>
            <p className="text-[9px] text-muted-foreground truncate flex items-center gap-1">
              {isVendor ? <UserCog className="w-2.5 h-2.5" /> : null}
              {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Administrador' : user?.role === 'guard' ? 'Guardia' : 'Promotor'}
            </p>
          </div>
        </div>
        <Button variant="ghost" onClick={toggleTheme} className="w-full justify-start gap-2 text-xs h-8 text-muted-foreground hover:text-white" data-testid="theme-toggle-btn">
          {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          {theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
        </Button>
        <Button variant="ghost" onClick={logout} className="w-full justify-start gap-2 text-xs h-8 text-muted-foreground hover:text-white" data-testid="logout-btn">
          <LogOut className="w-3.5 h-3.5" /> Cerrar sesion
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-56 bg-[#0a0c12]/95 backdrop-blur-lg border-r border-white/[0.04] flex-col z-30" data-testid="sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#0a0c12] border-r border-white/[0.04] flex flex-col z-50 animate-in slide-in-from-left duration-200" data-testid="sidebar-mobile">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
